import { generateUUID } from "helpers";
import { getLungDashboard, getLungDashboardReferences } from "api/dashboardApi";
import { Filters } from "../plotdata";
import { IWorkerChartDataRequest, IWorkerChartDataResult, IWorkerFetchRequest, IWorkerFetchResult, IWorkerPingResult, IWorkerRequest, WorkerRequestType, WorkerResultType } from "./models";
import DataWorker from 'worker-loader!./data.worker';
export type { IWorkerChartDataResult, IWorkerFetchResult, IWorkerPingResult, IWorkerRequest, WorkerRequestType }

//#region Worker

let worker: DataWorker = null;
let promises = {};
let data = {
    minYear: 0,
}

/**
 * Start the Worker
 */
export function start(minYear: number) {
    data.minYear = minYear;

    if (!window["Worker"]) {
        console.log("WebWorker not supported");
        return false;

    } else {
        console.log("WebWorker starting");

        worker = worker || new DataWorker();

        /**
         * When the worker sends an error back to the main thread, resolve the associated promise.
         */
        worker.addEventListener("message", (ev: MessageEvent) => {
            const { data: { id, type, payload } } = ev;

            //Any specific handling by message type
            switch (type as WorkerResultType) {
                // case WorkerResultType.Ping: {
                // case WorkerResultType.Fetch: {
                default: {
                    //Default handler will just resolve
                    promises[id]?.resolve(payload);
                    break;
                }
            }

            promises[id] = undefined;
        });

        /**
         * When the worker sends an error back to the main thread, reject the associated promise.
         */
        worker.addEventListener("messageerror", (ev: MessageEvent) => {
            const { data: { id, type, payload } } = ev;

            //Any specific handling by message type
            switch (type) {
                //case WorkerResultType.Ping: {
                //case WorkerResultType.Fetch: {
                default: {
                    console.error("ERROR from worker", payload);
                    promises[id].reject(payload);
                    break;
                }
            }

            promises[id] = undefined;
        });

        return true;
    }
}

/**
 * Post the message to the Worker
 * @param request 
 */
function send(request: IWorkerRequest) {
    worker.postMessage(request);
}

/**
 * Transform into a Promise and track it.
 * @param type 
 * @param payload 
 */
function actionPromise<TIn, TOut>(type: WorkerRequestType, payload: TIn): Promise<TOut> {
    const id = generateUUID();
    promises[id] = {};
    promises[id].promise = new Promise((resolve, reject) => {
        send({ id, type, payload });

        promises[id].resolve = resolve;
        promises[id].reject = reject;
    });

    return promises[id].promise;
}

export function ping(): Promise<IWorkerPingResult> {
    return actionPromise<void, IWorkerPingResult>(WorkerRequestType.Ping, null);
}

/**
 * Fetch the data from the server, then push it to the Worker for processing.
 * @param all 
 */
export function fetch(all: boolean): Promise<IWorkerFetchResult> {
    //WebWorker cannot access sessionStorage so do the api call here and
    //send the data tp it.
    return Promise.all([
        getLungDashboard(all),
        getLungDashboardReferences(all),
    ])
        .then(results => {
            return actionPromise<IWorkerFetchRequest, IWorkerFetchResult>(WorkerRequestType.Fetch, {
                all,
                rawRows: results[0] as string,
                rawRefs: results[1] as any,
                minYear: data.minYear,
            });
        });

}

/**
 * Apply the filters in the Worker and return the ChartData
 * @param filtersToApply 
 */
export function chartData(filtersToApply: Filters): Promise<IWorkerChartDataResult> {
    return actionPromise<IWorkerChartDataRequest, IWorkerChartDataResult>(WorkerRequestType.ChartData, {
        filtersToApply,
        minYear: data.minYear,
    });
}

/** Terminate the worker */
export function stop() {
    worker && worker.terminate();
    worker = null;
    console.log("WebWorker terminated");
}

//#endregion Worker
